import { Controller, type UseFormReturn } from 'react-hook-form'

import { FieldError } from '../ui/FieldError'

import { EmailChipsInput } from './EmailChipsInput'
import { ImageDropzone } from './ImageDropzone'

import type { CompanyFormInput } from '../../types/forms'

interface CompanyFormFieldsProps {
  form: UseFormReturn<CompanyFormInput>
  disabled?: boolean
  currentImageUrl?: string | null
  currentImageName?: string | null
  idPrefix?: string
}

export function CompanyFormFields({
  form,
  disabled,
  currentImageUrl,
  currentImageName,
  idPrefix = 'create',
}: CompanyFormFieldsProps) {
  const {
    register,
    control,
    formState: { errors },
  } = form

  const emailError = errors.emails?.draft?.message ?? errors.emails?.root?.message
  const nameId = `${idPrefix}-company-name`
  const activityId = `${idPrefix}-business-activity`
  const websiteId = `${idPrefix}-website-url`
  const emailsId = `${idPrefix}-company-emails`

  return (
    <div className="grid gap-5">
      <div>
        <label className="form-label" htmlFor={nameId}>
          Nombre de la empresa
        </label>
        <input
          id={nameId}
          className="form-input"
          placeholder="Ej. Taller Horizonte"
          autoComplete="organization"
          maxLength={160}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? `${nameId}-error` : undefined}
          disabled={disabled}
          {...register('name')}
        />
        <FieldError id={`${nameId}-error`} message={errors.name?.message} />
      </div>

      <div>
        <label className="form-label" htmlFor={activityId}>
          Actividad o tipo de empresa
        </label>
        <input
          id={activityId}
          className="form-input"
          placeholder="Ej. Venta de muebles"
          maxLength={200}
          aria-invalid={Boolean(errors.businessActivity)}
          aria-describedby={errors.businessActivity ? `${activityId}-error` : undefined}
          disabled={disabled}
          {...register('businessActivity')}
        />
        <FieldError id={`${activityId}-error`} message={errors.businessActivity?.message} />
      </div>

      <div>
        <label className="form-label" htmlFor={websiteId}>
          URL del sitio web
        </label>
        <input
          id={websiteId}
          className="form-input"
          type="text"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          placeholder="empresa.com"
          aria-invalid={Boolean(errors.websiteUrl)}
          aria-describedby={errors.websiteUrl ? `${websiteId}-error` : undefined}
          disabled={disabled}
          {...register('websiteUrl')}
        />
        <FieldError id={`${websiteId}-error`} message={errors.websiteUrl?.message} />
      </div>

      <div>
        <label className="form-label" htmlFor={emailsId}>
          Correos electrónicos
        </label>
        <Controller
          name="emails"
          control={control}
          render={({ field }) => (
            <EmailChipsInput
              id={emailsId}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              error={emailError}
            />
          )}
        />
      </div>

      <div>
        <span className="form-label">Imagen</span>
        <Controller
          name="image"
          control={control}
          render={({ field }) => (
            <ImageDropzone
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
              error={errors.image?.message}
              currentImageUrl={currentImageUrl}
              currentImageName={currentImageName}
            />
          )}
        />
      </div>
    </div>
  )
}
